import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { PUBLIC_GIST_ROUTE } from "../../services/graphql/queriesMutations";
import type { CommentAnchorType, CommentType, GistType, SubjectType } from "../../services/types";
import { buildGistUrl, buildSubjectUrl, buildWorkspaceGistUrl, normalizeHashTarget, parseGistSlugWithId } from "../../utils/publicUrls";
import { sanitizeRichHtml } from "../../utils/richHtmlSanitizer";
import { absoluteBrowserUrl, applyPublicSeoMeta, copyToClipboard, getDisplayName, getLatestBody } from "./publicPageUtils";
import styles from "./PublicPages.module.css";

type PublicGistRouteData = {
  publicGistRoute: {
    canonicalUrl: string;
    shouldRedirect: boolean;
    subject: SubjectType;
    gist: GistType;
  };
};

const PublicGistPage: React.FC = () => {
  const { subjectSlug = "", gistSlugWithId = "" } = useParams();
  const navigate = useNavigate();
  const parsed = parseGistSlugWithId(gistSlugWithId);
  const { data, loading, error } = useQuery<PublicGistRouteData>(PUBLIC_GIST_ROUTE, {
    variables: {
      subjectSlug,
      gistSlug: parsed?.gistSlug || gistSlugWithId,
      gistPublicId: parsed?.gistPublicId || null,
    },
    skip: !subjectSlug || !gistSlugWithId,
  });

  const route = data?.publicGistRoute;
  const subject = route?.subject;
  const gist = route?.gist;
  const [copyStatus, setCopyStatus] = useState<string>("");
  const hashTarget = normalizeHashTarget(typeof window !== "undefined" ? window.location.hash : "");

  useEffect(() => {
    if (route?.shouldRedirect && route.canonicalUrl) {
      navigate(`${route.canonicalUrl}${window.location.hash || ""}`, { replace: true });
    }
  }, [navigate, route]);

  useEffect(() => {
    if (!gist || !route?.canonicalUrl) return;
    applyPublicSeoMeta({
      title: gist.seoTitle || gist.title,
      description: gist.seoDescription,
      canonicalPath: route.canonicalUrl,
      indexable: gist.status === "PUBLISHED" && gist.visibility === "PUBLIC" && subject?.visibility === "PUBLIC",
    });
  }, [gist, route, subject]);

  const handleCopy = async () => {
    if (!route?.canonicalUrl) return;
    const ok = await copyToClipboard(absoluteBrowserUrl(`${route.canonicalUrl}${window.location.hash || ""}`));
    setCopyStatus(ok ? "Copied" : "Copy failed");
    window.setTimeout(() => setCopyStatus(""), 1600);
  };

  useEffect(() => {
    if (!gist || !hashTarget) return;
    const timeoutId = window.setTimeout(() => {
      const target = document.getElementById(hashTarget);
      target?.scrollIntoView({ block: "start", behavior: "smooth" });
    }, 120);
    return () => window.clearTimeout(timeoutId);
  }, [gist, hashTarget]);

  const bodyHtml = useMemo(() => sanitizeRichHtml(getLatestBody(gist)), [gist]);
  const childGists = useMemo(() => {
    return (gist?.gists || [])
      .filter((child) => child.status === "PUBLISHED" && child.visibility === "PUBLIC")
      .slice()
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [gist]);

  if (loading) {
    return <div className={styles.page}><main className={styles.shell}>Loading public gist…</main></div>;
  }

  if (error || !subject || !gist) {
    return <div className={styles.page}><main className={styles.shell}><div className={styles.errorBox}>Public gist not found.</div></main></div>;
  }

  return (
    <div className={styles.page}>
      <main className={styles.shell}>
        <section className={styles.hero}>
          <p className={styles.eyebrow}>
            <Link className={styles.inlineLink} to={buildSubjectUrl(subject)}>{subject.title}</Link> · Public gist
          </p>
          <h1 className={styles.title}>{gist.seoTitle || gist.title}</h1>
          {gist.seoDescription ? <p className={styles.subtitle}>{gist.seoDescription}</p> : null}
          <div className={styles.metaRow}>
            <span className={styles.pill}>{gist.visibility || "PUBLIC"}</span>
            <span className={styles.pill}>{gist.status || "PUBLISHED"}</span>
            <span className={styles.pill}>Public gist</span>
          </div>
        </section>

        <div className={styles.grid}>
          <article className={styles.card}>
            <div className={styles.article} dangerouslySetInnerHTML={{ __html: bodyHtml || "<p>No public body text yet.</p>" }} />

            <hr />
            <h2 className={styles.sectionTitle}>Child gists</h2>
            <div className={styles.gistList}>
              {childGists.length ? childGists.map((child: GistType) => (
                <Link key={child.id} className={styles.gistLink} to={buildGistUrl(subject, child)}>
                  <h3 className={styles.gistTitle}>{child.title}</h3>
                  <p className={styles.gistExcerpt}>Open this related public gist.</p>
                </Link>
              )) : <p className={styles.empty}>No public child gists yet.</p>}
            </div>

            <hr />
            <h2 className={styles.sectionTitle}>Comments</h2>
            <div className={styles.commentList}>
              {(gist.comments || []).length ? (gist.comments || []).map((comment: CommentType) => {
                const elementId = `comment-${comment.publicId || comment.id}`;
                return (
                  <section key={comment.id} id={elementId} className={`${styles.comment} ${hashTarget === elementId ? styles.hashActive : ""}`}>
                    <p className={styles.smallText}>By {getDisplayName(comment.user)}</p>
                    <p>{comment.isRemoved ? "[removed]" : comment.comment}</p>
                  </section>
                );
              }) : <p className={styles.empty}>No public comments yet.</p>}
            </div>

            <hr />
            <h2 className={styles.sectionTitle}>Anchored discussions</h2>
            <div className={styles.commentList}>
              {(gist.commentAnchors || []).length ? (gist.commentAnchors || []).map((anchor: CommentAnchorType) => {
                const elementId = `anchor-${anchor.publicId || anchor.id}`;
                return (
                  <section key={anchor.id} id={elementId} className={`${styles.anchor} ${hashTarget === elementId ? styles.hashActive : ""}`}>
                    <p className={styles.smallText}>Selected text</p>
                    <blockquote>{anchor.selectedText}</blockquote>
                    <p>{anchor.rootComment?.comment}</p>
                  </section>
                );
              }) : <p className={styles.empty}>No anchored public discussions yet.</p>}
            </div>
          </article>

          <aside className={styles.sideCard}>
            <h2 className={styles.sectionTitle}>Canonical URL</h2>
            <p className={styles.smallText}>{route?.canonicalUrl}</p>
            <button type="button" className={styles.copyButton} onClick={handleCopy}>Copy public link</button>
            {copyStatus ? <p className={styles.copyStatus}>{copyStatus}</p> : null}
            <hr />
            <div className={styles.linkStack}>
              <Link to={buildSubjectUrl(subject)}>Back to subject</Link>
              <Link to={buildWorkspaceGistUrl(gist, "read")}>Open workspace reader</Link>
              <Link to={buildWorkspaceGistUrl(gist, "advanced")}>Open workspace advanced</Link>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default PublicGistPage;
